import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useAuth } from '@shared/components/AuthProvider';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { ProductPrices } from '../../DesignsGallery/components/GalleryItem/components/ProductPrices/ProductPrices';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { Link, useTranslation } from '@shared/core';
import { useDesignTemplates } from '../../DesignsGallery/useDesignTemplates';
import { GalleryTile } from '../../GalleryTile';
import { CardThumbnail } from '../../DesignsGallery/components/GalleryItem/components/CardThumbnail';
import { desktopAspect, mobileAspect, tileRowGap } from '../../DesignsGallery/components/CardsConfig';
import { getRelatedThemeIds } from '@apps/card/constants/fallbackThemes';

interface RelatedItemsProps {
  category: StationeryTemplateCategoryEnum;
  themeId: string;
  selectedColorwayId?: string;
}

// Monogram templates are monogram-creator files, not sellable products: the
// shop has no monogram category page, so a matching-designs tile for one leads
// nowhere. Exclude the category from the related-items query.
const CATEGORIES = Object.values(StationeryTemplateCategoryEnum).filter(c => c !== StationeryTemplateCategoryEnum.monogram);

export const RelatedItems: React.FC<RelatedItemsProps> = ({ category, themeId, selectedColorwayId }) => {
  const { tq } = useTranslation('stationery');
  const { isSuperAdmin } = useAuth();
  const isMobile = useIsMobileScreen();

  const { primary, fallback } = useMemo(() => getRelatedThemeIds(themeId, selectedColorwayId), [themeId, selectedColorwayId]);
  const themeIds = useMemo(() => [...primary, ...fallback], [primary, fallback]);
  const allTemplates = useDesignTemplates({ categories: CATEGORIES.filter(c => c !== category), themeIds, skipDigitalDesigns: true });

  // The fallback family only fills gaps: for any category the design's own
  // theme covers, drop the fallback templates so a Gala PDP doesn't show both
  // Gala and Doodle thank-you cards.
  const templates = useMemo(() => {
    if (!allTemplates?.length || !fallback.length) return allTemplates;
    const primaryIds = new Set(primary);
    const coveredCategories = new Set(allTemplates.filter(t => primaryIds.has(t.themeId)).map(t => t.category));
    return allTemplates.filter(t => primaryIds.has(t.themeId) || !coveredCategories.has(t.category));
  }, [allTemplates, primary, fallback]);

  const { getProductDetailsPagePath } = useCardsRouterContext();
  const { buttonInteracted } = useCardTelemetry();

  if (!templates?.length) return null;

  return (
    <Flex
      flexDirection="column"
      gap={{ _: 8, sm2: 10 }}
      paddingTop={{ _: 8, sm2: 10 }}
      paddingX={{ _: 6, sm2: 7 }}
      width="100%"
      marginBottom={10}
    >
      <TextV2
        fontWeight={{ sm2: 600 }}
        typographyVariant={{ _: 'hed4', sm2: 'display2' }}
        width="100%"
        textAlign="center"
      >
        {tq`Shop Matching Designs`}
      </TextV2>
      <Box
        display="grid"
        width="100%"
        gridTemplateColumns={{
          _: 'repeat(2, 1fr)',
          sm2: 'repeat(3, 1fr)',
          md3: 'repeat(4, 1fr)',
        }}
        columnGap={1}
        rowGap={{ _: 4, sm2: 6 }}
      >
        {templates.map(template => {
          if (!template.enabled && !isSuperAdmin) {
            return null;
          }

          return (
            <GalleryTile
              key={template.id}
              aspectRatio={isMobile ? mobileAspect : desktopAspect}
              alignContent="start"
              rowGap={tileRowGap}
              as={Link}
              to={getProductDetailsPagePath(
                'wedding',
                template.category,
                template.themeId,
                template.id,
                undefined,
                // Only the base theme shares this design's colorway system;
                // legacy color-suffixed and fallback themes have their own.
                template.themeId === themeId ? selectedColorwayId : undefined,
              )}
              onClick={() => buttonInteracted({ name: 'pdp_matchingDesignClicked', category: template.category })}
            >
              <CardThumbnail
                template={template}
                selectedColorwayId={template.themeId === themeId ? selectedColorwayId : undefined}
                fallbackImageUrl={(template.thumbnailUrl || template.digitalDesignThumbnailUrl) ?? undefined}
              />
              <GalleryTile.Content
                title={templateToTitle(template)}
                label={
                  <ProductPrices
                    template={template}
                    explicitPaperDigitalCategory
                  />
                }
              />
            </GalleryTile>
          );
        })}
      </Box>
    </Flex>
  );
};
